import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryApi,
  type Address,
  type AddressTypeValue,
  type CreateAddressRequest,
  type CreateLocationAddressRequest,
  type UpdateAddressRequest,
} from '../client';
import { organisationKeys } from './useOrganisation';

/**
 * Stable query-key factory for address-scoped caches. Apps invalidate via
 * these constants rather than hand-rolled tuples so cache surgery stays
 * consistent across screens.
 */
export const addressKeys = {
  all: ['directory', 'address'] as const,
  byId: (id: string) => ['directory', 'address', id] as const,
  /**
   * Cache slot for the per-party address list. Drives the "Additional
   * Addresses" card grid on `PartyDetailsPage`; keyed by `partyId` so
   * each party owns its own slot.
   */
  byParty: (partyId: string) => ['directory', 'address', 'party', partyId] as const,
  /**
   * Cache slot for the org-wide Locations grid (addresses filtered to
   * `Task` + `Location` types — i.e. anything that isn't a party's
   * Primary or Billing address). Keyed by `organisationId` so each org
   * gets its own list.
   */
  locations: (organisationId: string) =>
    ['directory', 'address', 'locations', organisationId] as const,
  /** Cache slot for a hydrate-by-ids batch (e.g. a booking worklist's endpoints). */
  byIds: (organisationId: string, ids: readonly string[]) =>
    ['directory', 'address', 'byIds', organisationId, ids.join(',')] as const,
};

/**
 * Fetch a single address by id. Pass `null` to keep the query disabled —
 * useful when the parent organisation has `mainOfficeAddressId === null`
 * (no address set up yet) so we don't fire a request that would 404.
 */
export function useAddress(
  id: string | null,
  options?: Omit<UseQueryOptions<Address, Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Address, Error>({
    queryKey: id ? addressKeys.byId(id) : addressKeys.all,
    queryFn: () => {
      if (!id) throw new Error('No address id');
      return directoryApi.getAddress(id);
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Hydrate a specific set of addresses in one round-trip. Dedups + sorts the ids
 * (so the cache key is stable regardless of input order) and stays disabled
 * until there's an org id and at least one id to fetch.
 */
export function useAddressesByIds(
  organisationId: string | null,
  ids: readonly string[],
  options?: Omit<UseQueryOptions<Address[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  const unique = Array.from(new Set(ids.filter(Boolean))).sort();
  return useQuery<Address[], Error>({
    queryKey: organisationId ? addressKeys.byIds(organisationId, unique) : addressKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('No organisation id');
      return directoryApi.getAddressesByIds(organisationId, unique);
    },
    enabled: !!organisationId && unique.length > 0,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch every address for a party (via the org-scoped endpoint with the
 * `partyId` query param). Pass `null` to keep the query disabled — useful
 * before the route param resolves or in create-mode of a Party Details
 * page where there's no party id yet.
 */
export function useAddressesByParty(
  organisationId: string | null,
  partyId: string | null,
  options?: Omit<UseQueryOptions<Address[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery<Address[], Error>({
    queryKey: partyId ? addressKeys.byParty(partyId) : addressKeys.all,
    queryFn: () => {
      if (!organisationId || !partyId) throw new Error('Missing organisationId / partyId');
      return directoryApi.getAddresses(organisationId, { partyId });
    },
    enabled: !!organisationId && !!partyId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Locations grid feed. The page surfaces three buckets of addresses
 * operators dispatch against:
 *   - `Task`     — collection / delivery point.
 *   - `Location` — standalone facility (port, terminal, weighbridge).
 *   - `Primary`  — a customer's main address (the BE returns every
 *                  Primary row; the LocationListPage post-filters to
 *                  rows where `partyType` is `Customer` or `Both` so
 *                  supplier primaries don't pollute the list).
 *
 * Billing rows never reach the page — they aren't physical delivery
 * points. Filtering happens server-side via the `addressTypes` query
 * param so the wire payload is already narrowed.
 *
 * `partyId` is intentionally omitted from the call — the grid shows
 * org-wide addresses, including the ones linked to parties (rendered
 * read-only). The DTO carries `partyName` + `partyType` inline so the
 * grid can build the "Open party" link without a second round-trip,
 * and so the FE filter on Customer/Both can run on the same payload.
 */
export function useLocations(
  organisationId: string | null,
  options?: Omit<UseQueryOptions<Address[], Error>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  const types: AddressTypeValue[] = ['Task', 'Location', 'Primary'];
  return useQuery<Address[], Error>({
    queryKey: organisationId
      ? addressKeys.locations(organisationId)
      : addressKeys.all,
    queryFn: () => {
      if (!organisationId) throw new Error('Missing organisationId');
      return directoryApi.getAddresses(organisationId, { addressTypes: types });
    },
    enabled: !!organisationId,
    staleTime: 60 * 1000,
    ...options,
  });
}

/**
 * Mutation wrapper around POST `/addresses`. The Organisation page uses this
 * when an org has no `mainOfficeAddressId` yet — the caller chains a
 * PUT `/organisations` to link the new id, so we invalidate both caches.
 */
export function useCreateAddress() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateAddressRequest>({
    mutationKey: [...addressKeys.all, 'create'],
    mutationFn: (body) => directoryApi.createAddress(body),
    onSuccess: (newId, vars) => {
      qc.invalidateQueries({ queryKey: addressKeys.byId(newId) });
      qc.invalidateQueries({ queryKey: organisationKeys.byId(vars.organisationId) });
      // Refresh the per-party list so the AdditionalAddresses card grid
      // surfaces the new row immediately. Without this the operator has
      // to refresh the page to see it.
      if (vars.partyId) {
        qc.invalidateQueries({ queryKey: addressKeys.byParty(vars.partyId) });
      }
      // Refresh the org-wide Locations grid — a new address may belong
      // there (Task / Location / Customer Primary). Invalidating per
      // org keeps other orgs' caches untouched.
      qc.invalidateQueries({ queryKey: addressKeys.locations(vars.organisationId) });
    },
  });
}

/**
 * Mutation wrapper around PUT `/addresses`. On success invalidates the
 * single-address cache so any open page refetches the canonical server copy.
 */
export function useUpdateAddress() {
  const qc = useQueryClient();
  return useMutation<string, Error, UpdateAddressRequest>({
    mutationKey: [...addressKeys.all, 'update'],
    mutationFn: (body) => directoryApi.updateAddress(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: addressKeys.byId(vars.addressId) });
      // Conservative blanket invalidation — any open per-party list that
      // includes this row needs to refetch. Cheap (parties don't accumulate
      // many addresses) and avoids tracking the partyId through the
      // mutation contract.
      qc.invalidateQueries({ queryKey: ['directory', 'address', 'party'] });
      // Same logic for the org-wide Locations grid — the row may be
      // displayed there (Task / Location / Customer Primary). Blanket
      // pattern catches every org cached on the client.
      qc.invalidateQueries({ queryKey: ['directory', 'address', 'locations'] });
    },
  });
}

/**
 * Mutation for creating a port / terminal Location address from route catalogue
 * data — no Google Places lookup required. The BE waives `fullAddress` and
 * `placeId` validation when `addressType == Location`. Returns the new address id.
 *
 * Used by `BookingFormFields` to silently seed missing port locations when an
 * operator picks a route whose departure or arrival port isn't yet in the org's
 * catalogue. The locations query is invalidated on success so the address list
 * refreshes in the background (map pins appear on the next render).
 */
export function useCreateLocationAddress() {
  const qc = useQueryClient();
  return useMutation<string, Error, CreateLocationAddressRequest>({
    mutationKey: [...addressKeys.all, 'create-location'],
    mutationFn: (body) => directoryApi.createLocationAddress(body),
    onSuccess: (newId, vars) => {
      qc.invalidateQueries({ queryKey: addressKeys.byId(newId) });
      qc.invalidateQueries({ queryKey: addressKeys.locations(vars.organisationId) });
      qc.invalidateQueries({ queryKey: organisationKeys.byId(vars.organisationId) });
    },
  });
}

/**
 * Mutation wrapper around DELETE `/addresses/{id}`. Caller passes
 * `partyId` (when known) for targeted cache invalidation; null partyId
 * falls back to invalidating every per-party list (org-scoped delete).
 */
export function useDeleteAddress() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { id: string; partyId: string | null }>({
    mutationKey: [...addressKeys.all, 'delete'],
    mutationFn: ({ id }) => directoryApi.deleteAddress(id),
    onSuccess: (_res, { id, partyId }) => {
      qc.removeQueries({ queryKey: addressKeys.byId(id) });
      if (partyId) {
        qc.invalidateQueries({ queryKey: addressKeys.byParty(partyId) });
      } else {
        qc.invalidateQueries({ queryKey: ['directory', 'address', 'party'] });
      }
      // Org-wide Locations grid blanket invalidation — see useUpdateAddress.
      qc.invalidateQueries({ queryKey: ['directory', 'address', 'locations'] });
    },
  });
}
